class GuideVideoPlayer extends HTMLElement {
  constructor() {
    super()
    this.openButton = this.querySelector('[data-guide-video-open]')
    this.overlay = this.querySelector('[data-guide-video-overlay]')
    this.dialog = this.querySelector('[data-guide-video-dialog]')
    this.closeButton = this.querySelector('[data-guide-video-close]')
    this.mount = this.querySelector('[data-guide-video-mount]')
    this.template = this.querySelector('[data-guide-video-template]')
    this.isInline = this.hasAttribute('data-guide-inline-player')
    this.loaded = false
    this.lastFocus = null

    this.handleOpen = this.handleOpen.bind(this)
    this.handleClose = this.handleClose.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleOverlayClick = this.handleOverlayClick.bind(this)
  }

  connectedCallback() {
    if (!this.openButton) return
    if (this.isInline && !this.querySelector('video')) return
    if (!this.isInline && !this.overlay) return

    this.openButton.addEventListener('click', this.handleOpen)
    this.closeButton?.addEventListener('click', this.handleClose)
    this.overlay?.addEventListener('click', this.handleOverlayClick)
    if (!this.isInline) {
      document.addEventListener('keydown', this.handleKeydown)
    } else {
      this.bindInlineVideoEvents()
    }
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.handleOpen)
    this.closeButton?.removeEventListener('click', this.handleClose)
    this.overlay?.removeEventListener('click', this.handleOverlayClick)
    document.removeEventListener('keydown', this.handleKeydown)
    this.pauseMedia()
  }

  handleOpen() {
    if (this.openButton?.disabled || this.openButton?.getAttribute('aria-disabled') === 'true') return

    if (this.isInline) {
      this.toggleInlineVideo()
      return
    }

    if (!this.overlay) return

    this.lastFocus = document.activeElement
    this.loadVideo()
    this.overlay.hidden = false
    document.body.classList.add('overflow-hidden')
    this.dialog?.focus()
  }

  handleClose() {
    if (!this.overlay) return

    this.overlay.hidden = true
    this.pauseMedia()

    if (!document.querySelector('[data-guide-video-overlay]:not([hidden])')) {
      document.body.classList.remove('overflow-hidden')
    }

    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus()
    }
  }

  handleKeydown(event) {
    if (event.code !== 'Escape' || this.overlay?.hidden) return
    this.handleClose()
  }

  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.handleClose()
    }
  }

  toggleInlineVideo() {
    const video = this.querySelector('video')
    if (!video) return

    if (video.paused) {
      this.classList.add('is-started')
      video.play?.().catch(() => {})
    } else {
      video.pause()
    }
  }

  bindInlineVideoEvents() {
    const video = this.querySelector('video')
    if (!video) return

    video.addEventListener('play', () => {
      this.classList.add('is-started', 'is-playing')
      this.openButton?.classList.add('is-started', 'is-playing')
    })

    video.addEventListener('pause', () => {
      this.classList.remove('is-playing')
      this.openButton?.classList.remove('is-playing')
    })

    video.addEventListener('ended', () => {
      this.classList.remove('is-playing')
      this.openButton?.classList.remove('is-playing')
    })
  }

  loadVideo(target = this.mount) {
    if (this.loaded || !this.template || !target) return

    const node = this.template.content.firstElementChild?.cloneNode(true)
    if (!node) return

    target.innerHTML = ''
    target.appendChild(node)
    this.loaded = true
  }

  pauseMedia() {
    this.querySelectorAll('video').forEach((video) => {
      video.pause()
      video.currentTime = 0
    })
    this.classList.remove('is-playing')
    this.openButton?.classList.remove('is-playing')
  }
}

if (!customElements.get('guide-video-player')) {
  customElements.define('guide-video-player', GuideVideoPlayer)
}
